/**
 * Public Announcements Page
 *
 * Public-facing display of active announcements.
 * No authentication required - designed for embedding or public viewing.
 * Applies studio theme (colors, fonts) for branded experience.
 */

import React, { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useGetPublicAnnouncementWidgetQuery } from '../../api/announcementsApi';
import Watermark from '../../components/common/Watermark';
import { shouldShowWatermark } from '../../utils/watermark';

const PublicAnnouncementsPage: React.FC = () => {
  const { studioSlug } = useParams<{ studioSlug: string }>();
  const contentRef = useRef<HTMLDivElement>(null);

  const {
    data: announcementData,
    isLoading,
    error,
  } = useGetPublicAnnouncementWidgetQuery(studioSlug || '', {
    skip: !studioSlug,
  });

  // Extract theme colors (must be before conditional returns)
  const { config, announcements, theme } = announcementData || {};
  const fontFamily =
    (theme?.tokens?.typography as any)?.fontFamily?.body || 'Arial, sans-serif';
  const textColor = (theme?.tokens?.colors as any)?.text?.primary || '#000000';
  const backgroundColor =
    (theme?.tokens?.colors as any)?.background || '#ffffff';

  // Set document body and html background color to match theme
  useEffect(() => {
    const originalBodyBg = document.body.style.backgroundColor;
    const originalHtmlBg = document.documentElement.style.backgroundColor;

    document.body.style.backgroundColor = backgroundColor;
    document.documentElement.style.backgroundColor = backgroundColor;

    return () => {
      document.body.style.backgroundColor = originalBodyBg;
      document.documentElement.style.backgroundColor = originalHtmlBg;
    };
  }, [backgroundColor]);

  // Post height to parent window for iframe resizing
  useEffect(() => {
    if (!announcementData) {
      return undefined;
    }

    const postHeight = () => {
      if (contentRef.current && window.parent) {
        const height = contentRef.current.offsetHeight;
        window.parent.postMessage(
          {
            type: 'announcements-widget-height',
            height: height + 20, // Add some padding
          },
          '*'
        );
      }
    };

    // Post height after content loads
    postHeight();

    // Use ResizeObserver to detect content changes
    const resizeObserver = new ResizeObserver(() => {
      postHeight();
    });

    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [announcementData]);

  // Loading state
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress size={50} />
      </Box>
    );
  }

  // Error state
  if (error || !announcementData) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Alert severity="error">
          Unable to load announcements. Please check the URL and try again.
        </Alert>
      </Container>
    );
  }

  return (
    <Box
      ref={contentRef}
      sx={{
        width: '100%',
        backgroundColor,
        fontFamily,
        py: 2,
        px: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Box sx={{ display: 'inline-block', maxWidth: '100%' }}>
        {/* Centered Title - Only show if greeting message exists */}
        {config?.greeting_message && (
          <Box mb={2} textAlign="center">
            <Typography
              variant="h6"
              component="h1"
              sx={{
                fontFamily,
                color: textColor,
                fontWeight: 'bold',
              }}
            >
              {config.greeting_message}
            </Typography>
          </Box>
        )}

        {/* Announcements List - Vertically Stacked */}
        {!announcements || announcements.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              fontFamily,
              color: textColor,
              textAlign: 'center',
              opacity: 0.7,
            }}
          >
            No announcements at this time.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {announcements.map(announcement => (
              <Box
                key={`${announcement.title}-${announcement.date_range_display}`}
              >
                {announcement.title && (
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontFamily,
                      color: textColor,
                      fontWeight: 'bold',
                      mb: 0.5,
                    }}
                  >
                    {announcement.title}
                  </Typography>
                )}
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily,
                    color: textColor,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {announcement.message}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Watermark (for free tier only) */}
        <Watermark
          textColor={textColor}
          shouldDisplay={shouldShowWatermark(
            announcementData.studio.owner_plan_slug
          )}
        />
      </Box>
    </Box>
  );
};

export default PublicAnnouncementsPage;
